class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this._current = 0;
    this._interval = null;
    this._slides = [];
    this._onVis = () => (document.hidden ? this.pause() : this.resume());
  }

  connectedCallback() {
    this.addEventListener("mouseenter", this._onEnter);
    this.addEventListener("mouseleave", this._onLeave);
    document.addEventListener("visibilitychange", this._onVis);

    this._collectSlides();
    this._applyAria();
    this.play();
  }

  disconnectedCallback() {
    document.removeEventListener("visibilitychange", this._onVis);
    this.removeEventListener("mouseenter", this._onEnter);
    this.removeEventListener("mouseleave", this._onLeave);
    this.suspend();
  }

  _onEnter = () => this.suspend();
  _onLeave = () => this.resume();

  _collectSlides() {
    this._slides = Array.from(
      this.querySelectorAll('[ref="slides"], .announcement-bar__slide')
    );
  }

  get autoplayInterval() {
    const v = parseInt(this.getAttribute("autoplay") || "", 10);
    return Number.isFinite(v) && v > 0 ? v * 1000 : 0;
  }

  get autoplay() {
    return this.autoplayInterval > 0;
  }

  next() {
    this.current = this._current + 1;
  }

  previous() {
    this.current = this._current - 1;
  }

  play() {
    if (!this.autoplay) return;
    this.paused = false;

    this.suspend();
    const interval = this.autoplayInterval;

    this._interval = setInterval(() => {
      if (this.matches(":hover") || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  suspend() {
    if (this._interval) {
      clearInterval(this._interval);
      this._interval = null;
    }
  }

  resume() {
    if (!this.autoplay || this.paused) return;
    this.play();
  }

  get paused() {
    return this.hasAttribute("paused");
  }

  set paused(v) {
    this.toggleAttribute("paused", !!v);
  }

  get current() {
    return this._current;
  }

  set current(v) {
    this._current = Number(v) || 0;
    this._applyAria();
  }

  _applyAria() {
    this._collectSlides();

    const slides = this._slides || [];
    const n = slides.length;
    if (!n) return;

    let idx = this._current % n;
    if (idx < 0) idx += n;

    slides.forEach((el, i) => {
      const active = i === idx;
      el.setAttribute("aria-hidden", String(!active));
      el.setAttribute("role", "group");
      el.setAttribute("aria-roledescription", "slide");
    });
  }
}

if (!customElements.get("announcement-bar-component")) {
  customElements.define("announcement-bar-component", AnnouncementBar);
}

(function () {
  const CC_TZ = {
    BR: "America/Sao_Paulo",
    PT: "Europe/Lisbon",
    ES: "Europe/Madrid",
    FR: "Europe/Paris",
    DE: "Europe/Berlin",
    IT: "Europe/Rome",
    NL: "Europe/Amsterdam",
    BE: "Europe/Brussels",
    CH: "Europe/Zurich",
    AT: "Europe/Vienna",
    GB: "Europe/London",
    IE: "Europe/Dublin",
    US: "America/New_York",
    CA: "America/Toronto",
    MX: "America/Mexico_City",
    AR: "America/Argentina/Buenos_Aires",
    CL: "America/Santiago",
    CO: "America/Bogota",
    PE: "America/Lima",
    AU: "Australia/Sydney",
    NZ: "Pacific/Auckland",
    AE: "Asia/Dubai",
  };

  const root = document.documentElement;

  function getTZ(cc) {
    const up = String(cc || "").trim().toUpperCase();
    if (CC_TZ[up]) return CC_TZ[up];
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz || "UTC";
    } catch {
      return "UTC";
    }
  }

  function partsInTZ(tz) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(new Date());

    const m = {};
    for (const p of parts) if (p.type !== "literal") m[p.type] = p.value;

    return {
      y: +m.year,
      mo: +m.month,
      d: +m.day,
      h: +m.hour,
      mi: +m.minute,
      s: +m.second,
    };
  }

  function utcMsFromLocalParts(t) {
    return Date.UTC(t.y, t.mo - 1, t.d, t.h, t.mi, t.s);
  }

  function tickBar(bar) {
    const tz = getTZ(bar.getAttribute("data-ab-cc"));
    const nowParts = partsInTZ(tz);
    const now = utcMsFromLocalParts(nowParts);
    const next = Date.UTC(nowParts.y, nowParts.mo - 1, nowParts.d + 1, 0, 0, 0);

    let diff = next - now;
    if (diff < 0) diff = 0;

    const hEl = bar.querySelector("[data-ab-hours]");
    const mEl = bar.querySelector("[data-ab-minutes]");
    const sEl = bar.querySelector("[data-ab-seconds]");
    if (!hEl || !mEl || !sEl) return;

    const hrs = Math.floor(diff / 36e5);
    diff -= hrs * 36e5;
    const mins = Math.floor(diff / 6e4);
    diff -= mins * 6e4;
    const secs = Math.floor(diff / 1e3);

    const pad = (n) => String(n).padStart(2, "0");
    hEl.textContent = pad(hrs);
    mEl.textContent = pad(mins);
    sEl.textContent = pad(secs);
  }

  function setAbHeight() {
    const ab = document.querySelector(".announcement-bar");
    if (!ab) return;
    const h = Math.round(ab.getBoundingClientRect().height || 0);
    root.style.setProperty("--ab-h", h + "px");
  }

  function barsWithCountdown() {
    return Array.from(
      document.querySelectorAll(".announcement-bar[data-ab-root]")
    ).filter((bar) => bar.querySelector("[data-ab-countdown]"));
  }

  function tickAllCountdowns() {
    const bars = barsWithCountdown();
    for (let i = 0; i < bars.length; i++) tickBar(bars[i]);
  }

  let intervalId = null;
  function ensureInterval() {
    const has = barsWithCountdown().length > 0;
    if (!has) {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      return;
    }
    if (!intervalId) {
      tickAllCountdowns();
      intervalId = setInterval(tickAllCountdowns, 1000);
    }
  }

  setAbHeight();
  ensureInterval();

  let rt = 0;
  addEventListener(
    "resize",
    () => {
      clearTimeout(rt);
      rt = setTimeout(() => {
        setAbHeight();
        ensureInterval();
      }, 120);
    },
    { passive: true }
  );

  addEventListener(
    "pageshow",
    () => {
      setAbHeight();
      ensureInterval();
    },
    { passive: true }
  );

  document.addEventListener(
    "visibilitychange",
    () => {
      if (!document.hidden) {
        setAbHeight();
        ensureInterval();
      }
    },
    { passive: true }
  );

  const rootEl = document.querySelector("#header-group") || document.body || document.documentElement;
  try {
    const mo = new MutationObserver(() => {
      setAbHeight();
      ensureInterval();
    });
    mo.observe(rootEl, { childList: true, subtree: true });
  } catch {}
})();
